import './HowItWorksFlow.css';

/**
 * The value loop, drawn.
 *
 * The thing people get wrong about SUSU Closet is thinking they are paying for
 * a try-on button. They are paying for the wardrobe — pieces by designers who
 * get paid every time someone wears one. This section exists to make that
 * concrete: where a piece comes from, what a try-on costs, and where the money
 * lands.
 *
 * The numbers here are the real ones from the backend (2 credits per shared
 * try-on, $0.10 to the designer, $10 payout minimum). If those change in
 * api/credit/services/credit.js or try-on-earning, change them here too.
 */
const HowItWorksFlow = () => (
  <section className="hiw" aria-labelledby="hiw-title">
    <span className="hiw-eyebrow">HOW IT WORKS</span>
    <h2 id="hiw-title">
      A closet of real designers. <em>Who get paid when you wear them.</em>
    </h2>
    <p className="hiw-lede">
      Anyone can generate a picture of clothes. What you cannot generate is a
      piece an actual designer made — and a way for them to earn from it. Every
      look here belongs to someone, and wearing it pays them.
    </p>

    <div className="hiw-diagram-wrap">
      <svg
        className="hiw-diagram"
        viewBox="0 0 880 300"
        role="img"
        aria-labelledby="hiw-diagram-title hiw-diagram-desc"
      >
        <title id="hiw-diagram-title">How a designer earns from a try-on</title>
        <desc id="hiw-diagram-desc">
          A designer lists a piece on SHEYOU. It appears in the SUSU Closet
          community, where our ambassadors wear it daily. A member tries it on
          for two credits: one goes to the platform, one becomes ten cents for
          the designer. Their balance pays out through Stripe once it reaches
          ten dollars.
        </desc>

        <defs>
          <marker id="hiw-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                  markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" className="hiw-arrowhead" />
          </marker>
        </defs>

        {/* Row 1: how a piece reaches a member */}
        <g className="hiw-node">
          <rect x="10" y="40" width="180" height="76" rx="12" />
          <text x="100" y="68" className="hiw-node-title">Designer lists a piece</text>
          <text x="100" y="88" className="hiw-node-sub">on SHEYOU — free,</text>
          <text x="100" y="103" className="hiw-node-sub">no store to run</text>
        </g>

        <line x1="196" y1="78" x2="242" y2="78" className="hiw-link" markerEnd="url(#hiw-arrow)" />

        <g className="hiw-node">
          <rect x="248" y="40" width="180" height="76" rx="12" />
          <text x="338" y="68" className="hiw-node-title">It enters the closet</text>
          <text x="338" y="88" className="hiw-node-sub">shown in Explore and</text>
          <text x="338" y="103" className="hiw-node-sub">worn by our ambassadors</text>
        </g>

        <line x1="434" y1="78" x2="480" y2="78" className="hiw-link" markerEnd="url(#hiw-arrow)" />

        <g className="hiw-node">
          <rect x="486" y="40" width="180" height="76" rx="12" />
          <text x="576" y="68" className="hiw-node-title">A member wears it</text>
          <text x="576" y="88" className="hiw-node-sub">one try-on costs</text>
          <text x="576" y="103" className="hiw-node-sub">2 credits</text>
        </g>

        {/* The split */}
        <path d="M 576 122 L 576 150" className="hiw-link" markerEnd="url(#hiw-arrow)" />

        <g className="hiw-node hiw-node--split">
          <rect x="430" y="156" width="292" height="52" rx="12" />
          <text x="576" y="178" className="hiw-node-title">Those 2 credits split</text>
          <text x="576" y="196" className="hiw-node-sub">1 keeps the platform running · 1 is the designer&rsquo;s</text>
        </g>

        {/* Row 2: the money, right to left, closing the loop */}
        <path d="M 424 182 L 320 182" className="hiw-link" markerEnd="url(#hiw-arrow)" />

        <g className="hiw-node hiw-node--money">
          <rect x="132" y="150" width="182" height="64" rx="12" />
          <text x="223" y="176" className="hiw-node-amount">$0.10</text>
          <text x="223" y="197" className="hiw-node-sub">to the designer, every wear</text>
        </g>

        <path d="M 223 220 L 223 246" className="hiw-link" markerEnd="url(#hiw-arrow)" />

        <g className="hiw-node hiw-node--money">
          <rect x="92" y="252" width="262" height="40" rx="12" />
          <text x="223" y="277" className="hiw-node-sub">
            Paid out by Stripe once the balance reaches $10
          </text>
        </g>
      </svg>
    </div>

    <div className="hiw-points">
      <div>
        <h3>Nothing to set up</h3>
        <p>
          List a piece and it starts working. No shop, no stock, no shipping —
          unless you want to rent or sell it, which you can turn on any time.
        </p>
      </div>
      <div>
        <h3>You keep your work</h3>
        <p>
          Listing is not a sale. You set the licensing, rental and purchase
          terms, and you can take a piece down whenever you like.
        </p>
      </div>
      <div>
        <h3>Worn every day</h3>
        <p>
          Our ambassadors style new pieces daily and post them. Every wear is
          another designer earning, and another reason to subscribe.
        </p>
      </div>
    </div>
  </section>
);

export default HowItWorksFlow;
